import React, { useEffect, Suspense, lazy } from 'react';
import { initPixel, setupGlobalTracking, track } from './lib/pixel';
import { useSeo } from './lib/seo';
import { LazyMount } from './components/LazyMount';
const SocialProof = lazy(() => import('./components/SocialProof').then(m => ({
  default: m.SocialProof
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const ProductShowcase = lazy(() => import('./components/ProductShowcase').then(m => ({
  default: m.ProductShowcase
})));
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Countdown = lazy(() => import('./components/Countdown').then(m => ({
  default: m.Countdown
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const DarkCTA = lazy(() => import('./components/DarkCTA').then(m => ({
  default: m.DarkCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
const HERO_BASE = 'https://res.cloudinary.com/dowzlbqlz/image/upload';
const HERO_PATH = '/v1778382523/hf_20260509_035726_3b0b453a-cd00-4b5f-b77d-c15b35a1d726-Photoroom_1_g1m6xf.webp';
const HERO_IMG = `${HERO_BASE}/f_auto,q_auto,w_640,c_limit${HERO_PATH}`;
const HERO_SRCSET = `${HERO_BASE}/f_auto,q_auto,w_400,c_limit${HERO_PATH} 400w, ${HERO_BASE}/f_auto,q_auto,w_640,c_limit${HERO_PATH} 640w`;
export function App() {
  useSeo();
  useEffect(() => {
    // Load the Meta Pixel OFF the critical path so it never competes with the
    // hero LCP image or initial render. It boots on the first user interaction
    // (scroll/pointer/touch/key) or, failing that, once the browser is idle.
    let started = false;
    const boot = () => {
      if (started) return;
      started = true;
      cleanup();
      initPixel();
      setupGlobalTracking();
    };
    const events: (keyof WindowEventMap)[] = ['scroll', 'pointerdown', 'touchstart', 'keydown'];
    const opts: AddEventListenerOptions = {
      once: true,
      passive: true
    };
    const cleanup = () => {
      events.forEach(evt => window.removeEventListener(evt, boot, opts));
    };
    events.forEach(evt => window.addEventListener(evt, boot, opts));
    const ric = (window as any).requestIdleCallback;
    const idleId = ric ? ric(boot, {
      timeout: 4000
    }) : window.setTimeout(boot, 3500);
    return () => {
      cleanup();
      const cic = (window as any).cancelIdleCallback;
      if (ric && cic) cic(idleId);else clearTimeout(idleId as number);
    };
  }, []);
  return <>
      <header className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-5 sm:px-6 py-12 sm:py-16" role="banner" style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundImage: 'linear-gradient(135deg, rgb(240,255,244) 0%, rgb(220,252,231) 30%, rgb(240,255,244) 60%, rgb(248,250,252) 100%)',
      backgroundColor: 'rgb(255, 255, 255)'
    }} data-id="element-0">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-green-600/30 bg-white/80 px-4 py-1.5 shadow-sm" data-id="element-1">
          <span className="text-xs sm:text-sm font-bold text-green-700 tracking-wide uppercase" data-id="element-2">
            🥑 Organización Profesional · Nutricionistas · Argentina
          </span>
        </div>

        <h1 className="font-black max-w-2xl text-gray-900 m-0 mb-3 text-[28px] leading-[34px] sm:text-4xl sm:leading-[44px] md:text-5xl md:leading-[56px] tracking-tight" style={{
        textWrap: 'balance'
      } as React.CSSProperties} data-id="element-3">
          Dejá de sentir que tu consultorio es un caos.{' '}
          <span className="text-green-700" data-id="element-4">
            Organizá tus consultas con un sistema claro.
          </span>
        </h1>

        <div className="my-6 sm:my-8 w-full max-w-[320px] sm:max-w-[380px] aspect-square relative" data-id="element-5">
          <div className="absolute inset-4 rounded-full z-0 pointer-events-none" aria-hidden="true" style={{
          background: 'radial-gradient(circle, rgba(34,197,94,0.30) 0%, rgba(34,197,94,0.10) 50%, transparent 70%)'
        }} data-id="element-6" />
          <img src={HERO_IMG} srcSet={HERO_SRCSET} sizes="(max-width: 640px) 320px, 380px" alt="Megapack Nutrición Resuelta — Sistema de organización, planificación y sesiones para nutricionistas" width="400" height="400" fetchPriority="high" decoding="async" className="relative z-10 block max-w-full w-full h-auto object-contain" data-id="element-7" />
        </div>

        <p className="max-w-xl font-semibold text-gray-600 m-0 mb-6 text-center text-[17px] leading-relaxed sm:text-xl sm:leading-relaxed" data-id="element-8">
          <span className="font-black text-green-700" data-id="element-9">6 tomos + 5 bonos</span>{' '}
          para{' '}
          <span className="font-bold text-gray-900" data-id="element-10">
            organizar tu consultorio
          </span>
          , <span className="font-bold text-gray-900" data-id="element-11">ahorrar tiempo</span> y
          dejar de <span className="font-bold text-gray-900" data-id="element-12">improvisar</span>{' '}
          cada consulta.
        </p>

        <div className="mb-7 grid grid-cols-2 gap-2 w-full max-w-sm mx-auto" data-id="element-13">
          {['📄 100% PDF', '📖 Planificacion', '🇦🇷 Argentina', '🥑 Para Nutricionistas', '📚 6 Tomos + 5 Bonos', '🧠 Organización'].map(tag => <span key={tag} className="flex items-center justify-center text-center whitespace-nowrap bg-white/80 text-xs sm:text-sm font-semibold text-gray-900 leading-5 rounded-full px-3 py-1.5 shadow-sm" style={{
          border: '1px solid rgba(21,128,61,0.30)'
        }} data-id="element-14">
              {tag}
            </span>)}
        </div>

        <a href="#pricing" onClick={e => {
        e.preventDefault();
        track('Lead', {
          content_name: 'Hero CTA Click',
          cta_location: 'hero'
        });
        const target = document.getElementById('pricing');
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }} className="block w-full max-w-sm bg-green-700 text-white text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-5 no-underline text-center shadow-lg hover:opacity-90 transition-opacity" data-id="element-15">
          🥑 → QUIERO ORGANIZAR MI CONSULTORIO
        </a>

        <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 m-0" data-id="element-16">
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-17">
            🛡️ Garantía 7 días
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-18">
            🔒 Pago seguro
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-19">
            💳 Mercado Pago | Tarjeta | Transferencia
          </span>
        </div>
      </header>

      <main data-id="element-20">
        <Suspense fallback={null} data-id="element-21">
          <SocialProof data-id="element-22" />
        </Suspense>

        <Suspense fallback={null} data-id="element-23">
          <LazyMount minHeight={500} data-id="element-24">
            <PainPoints data-id="element-25" />
          </LazyMount>
          <LazyMount minHeight={700} data-id="element-26">
            <ProductShowcase data-id="element-27" />
          </LazyMount>
          <LazyMount minHeight={600} data-id="element-28">
            <WhatYouGet data-id="element-29" />
          </LazyMount>
          <LazyMount minHeight={800} data-id="element-30">
            <Bonuses data-id="element-31" />
          </LazyMount>
          <LazyMount minHeight={400} data-id="element-32">
            <Countdown data-id="element-33" />
          </LazyMount>
          <LazyMount minHeight={600} id="pricing" data-id="element-34">
            <Pricing data-id="element-35" />
          </LazyMount>
          <LazyMount minHeight={500} data-id="element-36">
            <Testimonials data-id="element-37" />
          </LazyMount>
          <LazyMount minHeight={300} data-id="element-38">
            <Guarantee data-id="element-39" />
          </LazyMount>
          <LazyMount minHeight={500} data-id="element-40">
            <FAQ data-id="element-41" />
          </LazyMount>
          <LazyMount minHeight={700} data-id="element-42">
            <DarkCTA data-id="element-43" />
          </LazyMount>
        </Suspense>
      </main>

      <Suspense fallback={null} data-id="element-44">
        <LazyMount minHeight={200} data-id="element-45">
          <Footer data-id="element-46" />
        </LazyMount>
        <PurchaseNotifications data-id="element-47" />
      </Suspense>
    </>;
}